import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { MailIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { cn } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx'
import { Input } from '@/components/ui/input.tsx'
import { authClient } from '@/clients/auth'
import { useAuth } from '@/features/auth/use-auth.ts'

export const Route = createFileRoute('/_guest/auth/login')({
  component: Login,
})

const authErrorMessages: Record<string, string> = {
  RATE_LIMITED: 'Troppe richieste di autenticazione. Riprovare tra poco',
  USER_NOT_FOUND: 'Utente non trovato. Contatta un amministratore.',
}

function Login() {
  const form = useForm<{ email: string }>()
  const signInMutation = useMutation<void, Error, { email: string }>({
    mutationFn: async (variables) => {
      const { error } = await authClient.signIn.magicLink({
        email: variables.email,
        callbackURL: '/',
      })

      if (error) {
        form.setError('email', {
          type: 'custom',
          message: authErrorMessages[error.code ?? ''] ?? 'Email non valida',
        })

        throw new Error(error.message)
      }
    },
  })
  const onSubmit = () => {
    return signInMutation.mutateAsync(form.getValues())
  }

  const auth = useAuth()

  if (auth.session) {
    return <Navigate to="/" />
  }

  return (
    <>
      <div className="container relative h-svh flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
        <div className="relative hidden h-full flex-col bg-cyan-200 bg-[url('https://picsum.photos/1600/1000/')] bg-cover bg-center p-10 text-white dark:border-r lg:flex">
          {/*<div className="absolute inset-0 bg-zinc-900" />*/}
          <div className="relative z-20 flex items-center text-lg font-medium">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2 size-6"
            >
              <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
            </svg>
            Ocarina
          </div>
          {/*<div className="relative z-20 mt-auto">*/}
          {/*  <blockquote className="space-y-2">*/}
          {/*    <p className="text-lg">*/}
          {/*      &ldquo;This library has saved me countless hours of work and*/}
          {/*      helped me deliver stunning designs to my clients faster than*/}
          {/*      ever before.&rdquo;*/}
          {/*    </p>*/}
          {/*    <footer className="text-sm">Sofia Davis</footer>*/}
          {/*  </blockquote>*/}
          {/*</div>*/}
        </div>
        <div className="h-full lg:p-8">
          <div className="mx-auto flex size-full flex-col justify-center space-y-6 sm:w-[350px]">
            <div className="flex flex-col space-y-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                Ocarina Login
              </h1>
              <p className="text-sm text-muted-foreground">
                Inserisci il tuo indirizzo email
              </p>
            </div>
            <div className={cn('grid gap-6')}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-2">
                    <div className="grid gap-1">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="name@example.com"
                                type="email"
                                autoCapitalize="none"
                                autoComplete="email"
                                autoCorrect="off"
                                {...field}
                              />
                            </FormControl>
                            <FormDescription></FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Button
                      disabled={
                        signInMutation.isPending ||
                        form.formState.isSubmitSuccessful
                      }
                    >
                      Login
                    </Button>
                  </div>
                </form>
              </Form>

              {form.formState.isSubmitSuccessful && (
                <Alert className="bg-green-50">
                  <MailIcon className="size-4" />
                  <AlertTitle>Email inviata</AlertTitle>
                  <AlertDescription>
                    Ti abbiamo inviato un'email con il link di accesso.
                    Controlla la tua posta per completare l'accesso!
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
